import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Search, Plus, RefreshCw, SlidersHorizontal, LayoutGrid
} from "lucide-react";
import { BACKEND_URL, DEAL_STATUS_CFG } from "./networkConstants";
import { DealCard } from "./NetworkShared";

const DEAL_TYPES = [
  { key: "all",          label: "All Types"   },
  { key: "wholesale",    label: "Wholesale"   },
  { key: "subject_to",   label: "SubTo"       },
  { key: "fix_and_flip", label: "Fix & Flip"  },
  { key: "brrrr",        label: "BRRRR"       },
  { key: "rental",       label: "Rental"      },
  { key: "multifamily",  label: "Multifamily" },
];

const ROI_RANGES = [
  { key: "all",   label: "All ROI"  },
  { key: "30+",   label: "30%+",  min: 30           },
  { key: "20-30", label: "20–30%", min: 20, max: 30 },
  { key: "10-20", label: "10–20%", min: 10, max: 20 },
  { key: "0-10",  label: "0–10%",  min:  0, max: 10 },
];

const PRICE_RANGES = [
  { key: "all",       label: "All Prices"       },
  { key: "under100k", label: "Under $100K",              max: 100000             },
  { key: "100-250k",  label: "$100K–$250K", min: 100000, max: 250000             },
  { key: "250-500k",  label: "$250K–$500K", min: 250000, max: 500000             },
  { key: "500k+",     label: "$500K+",      min: 500000                          },
];

export function DealBoardTab({ user, onDealClick }) {
  const navigate = useNavigate();
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [showFilters, setShowFilters] = useState(false);
  const [filterType, setFilterType] = useState("all");
  const [filterROI, setFilterROI] = useState("all");
  const [filterPrice, setFilterPrice] = useState("all");
  const [watchlistedIds, setWatchlistedIds] = useState(new Set());

  useEffect(() => {
    fetchDeals();
    axios.get(`${BACKEND_URL}/api/network/watchlist`, { withCredentials: true })
      .then(r => setWatchlistedIds(new Set(r.data?.deal_ids || [])))
      .catch(() => {});
  }, []);

  const fetchDeals = async (q = "") => {
    setLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/deals/network-board`, {
        params: { search: q || undefined }, withCredentials: true
      });
      setDeals(res.data?.deals || []);
    } catch { toast.error("Failed to load deal board"); }
    finally { setLoading(false); }
  };

  const applyFilters = (deal) => {
    if (filterStatus !== "all" && (deal.deal_status || "live") !== filterStatus) return false;
    if (filterType !== "all") {
      const strat = (deal.marketplace_strategy || "").toLowerCase();
      const type  = filterType.toLowerCase();
      if (!strat.includes(type)
        && !(type === "wholesale"    && strat.includes("wholesale"))
        && !(type === "subject_to"   && (strat.includes("sub") || strat.includes("subject")))
        && !(type === "fix_and_flip" && (strat.includes("flip") || strat.includes("fix")))
        && !(type === "brrrr"        && strat.includes("brrrr"))
        && !(type === "rental"       && (strat.includes("rental") || strat.includes("rent")))
        && !(type === "multifamily"  && (strat.includes("multi") || (deal.property_type || "").toLowerCase().includes("multi")))
      ) return false;
    }
    if (filterROI !== "all") {
      const arv = deal.arv || 0, purchase = deal.purchase_price || 0, rehab = deal.rehab_estimate || 0;
      const allIn = purchase + rehab;
      const roi = allIn > 0 && arv > 0 ? ((arv - allIn) / allIn) * 100 : null;
      const range = ROI_RANGES.find(r => r.key === filterROI);
      if (roi === null) return false;
      if (range.min != null && roi < range.min) return false;
      if (range.max != null && roi >= range.max) return false;
    }
    if (filterPrice !== "all") {
      const arv = deal.arv || deal.purchase_price || 0;
      const range = PRICE_RANGES.find(r => r.key === filterPrice);
      if (range.min != null && arv < range.min) return false;
      if (range.max != null && arv >= range.max) return false;
    }
    return true;
  };

  const filtered = deals.filter(applyFilters);
  const hasActiveFilters = filterStatus !== "all" || filterType !== "all" || filterROI !== "all" || filterPrice !== "all";

  return (
    <div>
      <div className="flex gap-2 mb-3">
        <div className="relative flex-1 min-w-0">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/30" />
          <input value={search}
            onChange={e => { setSearch(e.target.value); if (!e.target.value) fetchDeals(); }}
            onKeyDown={e => e.key === "Enter" && fetchDeals(search)}
            placeholder="Search by address…"
            data-testid="deal-board-search"
            className="w-full bg-[#0A0A0A] border border-white/[0.08] rounded-lg pl-9 pr-4 py-2 text-sm text-white placeholder:text-white/20 focus:outline-none focus:border-[#D4AF37]/30" />
        </div>
        <button onClick={() => setShowFilters(s => !s)} data-testid="toggle-filters-btn"
          className={`flex items-center gap-1.5 px-3 sm:px-4 py-2 border rounded-lg text-sm font-medium transition-all flex-shrink-0 ${
            showFilters || hasActiveFilters
              ? 'bg-[#D4AF37]/10 border-[#D4AF37]/40 text-[#D4AF37]'
              : 'bg-[#0A0A0A] border-white/[0.08] text-white/50 hover:text-white hover:border-white/20'
          }`}>
          <SlidersHorizontal className="w-4 h-4" />
          <span className="hidden sm:inline">Filters</span>
          {hasActiveFilters && <span className="w-1.5 h-1.5 rounded-full bg-[#D4AF37]" />}
        </button>
        <button onClick={() => navigate("/deals")} data-testid="post-deal-btn"
          className="flex items-center gap-2 px-3 sm:px-4 py-2 bg-[#D4AF37] hover:bg-[#C4A030] text-black font-bold text-sm rounded-lg transition-all flex-shrink-0">
          <Plus className="w-4 h-4" />
          <span className="hidden sm:inline">Post Deal</span>
        </button>
      </div>

      {showFilters && (
        <div className="bg-[#0D0D0D] border border-[#D4AF37]/15 rounded-xl p-4 mb-4 space-y-4 animate-fade-up">
          <div className="flex items-center justify-between">
            <p className="text-[10px] text-white/30 uppercase tracking-wider font-bold">Advanced Filters</p>
            {hasActiveFilters && (
              <button onClick={() => { setFilterStatus("all"); setFilterType("all"); setFilterROI("all"); setFilterPrice("all"); }}
                className="text-[10px] text-[#D4AF37]/60 hover:text-[#D4AF37] transition-colors">Clear All</button>
            )}
          </div>
          <div>
            <p className="text-[10px] text-white/30 uppercase tracking-wider mb-2">Deal Type</p>
            <div className="flex flex-wrap gap-1.5">
              {DEAL_TYPES.map(t => (
                <button key={t.key} onClick={() => setFilterType(t.key)}
                  className={`text-xs px-3 py-1.5 rounded-full border transition-all ${filterType === t.key ? 'bg-[#D4AF37]/15 border-[#D4AF37]/40 text-[#D4AF37]' : 'border-white/[0.08] text-white/40 hover:text-white'}`}>
                  {t.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <p className="text-[10px] text-white/30 uppercase tracking-wider mb-2">ROI Range</p>
            <div className="flex flex-wrap gap-1.5">
              {ROI_RANGES.map(r => (
                <button key={r.key} onClick={() => setFilterROI(r.key)}
                  className={`text-xs px-3 py-1.5 rounded-full border transition-all ${filterROI === r.key ? 'bg-blue-500/15 border-blue-500/40 text-blue-400' : 'border-white/[0.08] text-white/40 hover:text-white'}`}>
                  {r.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <p className="text-[10px] text-white/30 uppercase tracking-wider mb-2">Price Range (ARV)</p>
            <div className="flex flex-wrap gap-1.5">
              {PRICE_RANGES.map(r => (
                <button key={r.key} onClick={() => setFilterPrice(r.key)}
                  className={`text-xs px-3 py-1.5 rounded-full border transition-all ${filterPrice === r.key ? 'bg-emerald-500/15 border-emerald-500/40 text-emerald-400' : 'border-white/[0.08] text-white/40 hover:text-white'}`}>
                  {r.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="flex gap-2 mb-4 overflow-x-auto pb-1 scrollbar-none flex-nowrap">
        <button onClick={() => setFilterStatus("all")}
          className={`text-xs px-3 py-1.5 rounded-full border whitespace-nowrap transition-all flex-shrink-0 ${filterStatus === "all" ? 'bg-[#D4AF37]/10 border-[#D4AF37]/30 text-[#D4AF37]' : 'border-white/[0.06] text-white/40 hover:text-white'}`}>
          All {deals.length > 0 && <span className="ml-1 text-[9px] opacity-50">({filtered.length})</span>}
        </button>
        {Object.entries(DEAL_STATUS_CFG).map(([key, cfg]) => (
          <button key={key} onClick={() => setFilterStatus(key)}
            className={`text-xs px-3 py-1.5 rounded-full border whitespace-nowrap transition-all flex-shrink-0 ${filterStatus === key ? `${cfg.bg} ${cfg.border} ${cfg.color}` : 'border-white/[0.06] text-white/40 hover:text-white'}`}>
            {cfg.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20"><RefreshCw className="w-5 h-5 text-white/20 animate-spin" /></div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 bg-[#0A0A0A] border border-dashed border-white/[0.06] rounded-2xl">
          <div className="w-14 h-14 rounded-2xl bg-white/[0.03] border border-white/[0.06] flex items-center justify-center mx-auto mb-3">
            <LayoutGrid className="w-6 h-6 text-white/15" />
          </div>
          <p className="text-white/40 text-sm font-medium">No deals match this filter</p>
          <p className="text-white/20 text-xs mt-1">Post a deal or adjust the filters</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3">
          {filtered.map(deal => (
            <DealCard key={deal.deal_id} deal={deal} onClick={onDealClick}
              initialBookmarked={watchlistedIds.has(deal.deal_id)}
              onBookmarkChange={(id, saved) => setWatchlistedIds(prev => {
                const next = new Set(prev);
                saved ? next.add(id) : next.delete(id);
                return next;
              })}
            />
          ))}
        </div>
      )}
    </div>
  );
}
